import React, { memo, useMemo, useEffect } from 'react';
import cn from 'classnames';
import useSWRInfinite from 'swr/infinite';
import queryString from 'query-string';
import { useRecoilValue } from 'recoil';
import { useRouter } from 'next/router';
import { Icon, Spinner } from 'components';
import TouchpointCard from 'components/candidate/TouchpointCard';
import { Row } from 'components/layout';
import { Button } from 'components/form';
import { ButtonV3 } from 'components/ComponentV2';
import { httpHeadersState } from 'lib/atoms/userSecretAtom';
import { get } from 'lib/utils/http';
import { API_ROUTES } from 'lib/api-routes';
import {
  parsePaginatedResponse,
  parseResponse,
  parseArrayResponse,
} from 'lib/utils/parser';
import { PAGE_ROUTES } from 'lib/page-routes';
import {
  OpportunityAttributes,
  OpportunitiesResponse,
} from 'lib/models/opportunity';
import { CompanyAttributes } from 'lib/models/company';
import { StudentSocietyAttributes } from 'lib/models/student-society';
import { TouchpointType } from 'lib/models/touchpoint';
import { JobTouchpointable } from 'lib/models/job';
import { getOpportunityPublicPagePathname } from 'utils/urls';
import styles from '../Section.module.scss';

const PER_PAGE_COUNT = 3;

export interface OpportunityRecoProps {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
}

export const OpportunityReco: React.FC<OpportunityRecoProps> = memo(
  ({
    className = '', // custom class name
    style, // custom style
  }: OpportunityRecoProps) => {
    const router = useRouter();
    const { headers } = useRecoilValue(httpHeadersState);

    const endOfPageRef = React.useRef<HTMLDivElement | null>(null);
    const scrollToBottom = () => {
      endOfPageRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const getTouchpointListKey = (pageIndex: number) => {
      const params = {
        page: pageIndex + 1,
        per_page: PER_PAGE_COUNT,
        with_details: true,
      };
      const qs = queryString.stringify(params, {
        arrayFormat: 'bracket',
        skipEmptyString: true,
      });
      const pathname = `${API_ROUTES.CANDIDATE_TOUCHPOINT}${API_ROUTES.OPPORTUNITY_RECOMMENDATION}?${qs}`;

      return [pathname, headers];
    };

    const {
      data: touchpointsResponse,
      error: touchpointsError,
      size,
      setSize,
    } = useSWRInfinite<OpportunitiesResponse>(getTouchpointListKey, get, {
      revalidateOnFocus: false,
      revalidateFirstPage: false,
    });

    const isAPILoading = !touchpointsResponse && !touchpointsError;
    const isLoadingMore =
      isAPILoading ||
      (size > 0 &&
        touchpointsResponse &&
        typeof touchpointsResponse[size - 1] === 'undefined');

    const touchpoints = useMemo(() => {
      return parsePaginatedResponse<OpportunityAttributes>(touchpointsResponse);
    }, [touchpointsResponse]);

    const totalCount = touchpointsResponse
      ? touchpointsResponse[0]?.meta?.total
      : undefined;

    const hasMoreRecords = useMemo(() => {
      const firstApplicationList = Array.isArray(touchpointsResponse)
        ? touchpointsResponse[0]
        : null;
      if (firstApplicationList) {
        return (firstApplicationList?.meta?.total || 0) > size * PER_PAGE_COUNT;
      } else {
        return false;
      }
    }, [touchpointsResponse, size]);

    useEffect(() => {
      if (!isLoadingMore && size > 1) {
        scrollToBottom();
      }
    }, [isLoadingMore, size]);

    useEffect(() => {
      setSize(1);

      return () => {
        endOfPageRef.current = null;
        setSize(1);
      };
    }, []);

    const getTouchpointCard = (touchpoints: Array<OpportunityAttributes>) => {
      return (
        <div className={styles.touchpointcardList}>
          {touchpoints?.map(
            ({
              id,
              title,
              slug,
              touchpointable_type,
              start_date,
              kind,
              pay_currency,
              apply_url,
              touchpointable: touchpointableResponse,
              application_deadline,
              cities,
              is_eligible_for_auto_apply,
              creatable_for,
              cohost_applications: cohostApplicationsResponse,
            }) => {
              const createdBy = parseResponse<
                CompanyAttributes | StudentSocietyAttributes
              >(creatable_for);
              const { name, icon_url, is_verified, huzzle_verified } =
                createdBy;

              let maximumSalary, minimumSalary;
              if (
                touchpointable_type === TouchpointType.Job ||
                touchpointable_type === TouchpointType.Internship
              ) {
                const touchpointable = parseResponse(
                  touchpointableResponse as JobTouchpointable
                );
                maximumSalary = touchpointable?.maximum_salary;
                minimumSalary = touchpointable?.minimum_salary;
              }

              const publicPageUrl = `${
                window.location.origin
              }${getOpportunityPublicPagePathname(touchpointable_type, slug)}`;
              const cohost_applications = parseArrayResponse(
                cohostApplicationsResponse
              );

              return (
                <TouchpointCard
                  key={id}
                  icon_url={icon_url}
                  title={title}
                  createdBy={name}
                  startDate={start_date}
                  created_at={''}
                  application_deadline={application_deadline}
                  is_verified={is_verified}
                  touchpointable_type={touchpointable_type as TouchpointType}
                  maximum_salary={maximumSalary}
                  minimum_salary={minimumSalary}
                  pay_currency={pay_currency || 'GBP'}
                  isExpired={false}
                  url={publicPageUrl}
                  className={styles.touchpointcard}
                  isEasyApply={
                    touchpointable_type !== TouchpointType.Event && !apply_url
                  }
                  isNewSet={false}
                  huzzle_verified={huzzle_verified}
                  kind={kind}
                  isOnboarding
                  cities={cities}
                  id={id}
                  slug={slug}
                  cohost_applications={cohost_applications}
                  following={null}
                  apply_url={apply_url}
                  extension_installed={true}
                  showManagerProgress={false}
                  is_eligible_for_auto_apply={is_eligible_for_auto_apply}
                />
              );
            }
          )}
        </div>
      );
    };

    const contentClassNames = cn(
      {
        [styles.container]: true,
      },
      styles.section,
      className
    );

    return (
      <div
        className={contentClassNames}
        style={style}
        data-testid="OpportunityReco"
      >
        <p className={styles.title}>From companies you follow</p>
        <div ref={endOfPageRef}></div>

        {totalCount ? getTouchpointCard(touchpoints) : null}
        {totalCount === undefined || isLoadingMore ? (
          <Spinner
            size="small"
            className={cn(styles.spinner)}
            wrapper={totalCount === undefined ? true : false}
            wrapperClassname={cn({
              [styles['min-height']]: totalCount === undefined,
            })}
          />
        ) : null}
        {totalCount ? (
          <Row align="center" columnGap={8} className={styles.countsRow}>
            {touchpoints?.length ? (
              <p>
                Showing {touchpoints?.length} of {totalCount}
              </p>
            ) : null}
            {hasMoreRecords ? (
              <Button
                variant="text"
                color="blue"
                onClick={() => {
                  setSize(size + 1);
                }}
                className={styles.countsButton}
              >
                Show more
              </Button>
            ) : null}
          </Row>
        ) : null}
        {totalCount === 0 ? (
          <Row columnGap={10} wrap={false} className={styles.nodataSection}>
            <p className={styles.emoji}>
              <Icon className={styles.icon} iconName="office" size="auto" />
            </p>
            <div>
              <p className={styles.text}>
                There aren&apos;t any jobs, internships or events from companies
                you follow. Follow more companies so you don&apos;t miss out on
                any opportunities you may be interested in.
              </p>
              <Row columnGap={10}>
                <ButtonV3
                  color="blue"
                  variant="text"
                  onClick={() => router.push(PAGE_ROUTES.COMPANIES_SEARCH)}
                  className={styles.bluebutton}
                >
                  Explore companies
                </ButtonV3>
              </Row>
            </div>
          </Row>
        ) : null}
      </div>
    );
  }
);

OpportunityReco.displayName = 'OpportunityReco';
